// The code run in the case statements in this file should be wrapped in blocks.
/* eslint-disable no-case-declarations */

import * as React from 'react';
import {
    isRichTextAreaEnabled,
    isProformaTTVCUpdateMeasureEnabled,
    isNewTextFieldEnabledForForm,
    isUiModificationsSupportEnabled,
    isForgeCustomFieldsOnPortalRequestEnabled,
} from 'feature-flags';
import { isEqual as deepEquals, toNumber } from 'lodash';
import { FormattedMessage } from 'react-intl-next';
import { lazyForPaint } from 'react-loosely-lazy';
import SmoothCollapse from 'react-smooth-collapse';
import type {
    AssetsPickerState,
    AttachmentState,
    CascadingSelectState,
    CheckboxGroupState,
    CmdbObjectPickerState,
    DateState,
    EntitlementPickerState,
    FieldState,
    FieldValues,
    LabelPickerState,
    MultiSelectState,
    MultiServicePickerState,
    MultiUserPickerState,
    OrganisationPickerState,
    RadioGroupState,
    ReporterState,
    RequestCreateKBState,
    SelectState,
    TextAreaState,
    TextFieldState,
    UnknownState,
    UserPickerState,
} from 'state/persisted/request-create';
import { RequestCreatePropertyPanelModule } from 'view/forge-ui/request-create-property-panel';
import i18n from 'view/request-create-form/messages';
import { ButtonGroup } from 'view/request-create-form/styled';
import { withAnalyticsContext, withAnalyticsEvents, type WithAnalyticsEventsProps } from '@atlaskit/analytics-next';
import FeatureGates from '@atlaskit/feature-gate-js-client';
import UFOLoadHold from '@atlaskit/react-ufo/load-hold';
import Spinner from '@atlaskit/spinner';
import { OPERATIONAL_EVENT_TYPE } from '@atlassian/analytics-web-react';
import { CmdbObjectPicker } from '@atlassian/cmdb-help-center-components/src/ui/cmdb-object-picker';
import { AutoFocus } from '@atlassian/help-center-common-component/auto-focus';
import { REQUEST_CREATE_PANEL } from '@atlassian/help-center-common-component/connect-fragment';
import { AnonymousEmailField } from '@atlassian/help-center-common-component/fields/anonymous-email-field';
import { AssetsPicker } from '@atlassian/help-center-common-component/fields/assets-picker';
import { CascadingSelect } from '@atlassian/help-center-common-component/fields/cascading-select';
import { CheckboxGroup } from '@atlassian/help-center-common-component/fields/checkbox-group';
import { ConnectFields } from '@atlassian/help-center-common-component/fields/connect-fields';
import { DatePicker } from '@atlassian/help-center-common-component/fields/date-picker';
import { DateTimePicker } from '@atlassian/help-center-common-component/fields/date-time-picker';
import { EntitlementPicker } from '@atlassian/help-center-common-component/fields/entitlement-picker';
import { ForgeField } from '@atlassian/help-center-common-component/fields/forge-field';
import { LabelPicker } from '@atlassian/help-center-common-component/fields/label-picker';
import { MultiSelectField } from '@atlassian/help-center-common-component/fields/multi-select-field';
import { MultiServicePicker } from '@atlassian/help-center-common-component/fields/multi-service-picker';
import { MultiUserPicker } from '@atlassian/help-center-common-component/fields/multi-user-picker';
import { OrganisationPicker } from '@atlassian/help-center-common-component/fields/organisation-picker';
import type { RegisterProformaForm } from '@atlassian/help-center-common-component/fields/proforma';
import { RadioGroup } from '@atlassian/help-center-common-component/fields/radio-group';
import { Reporter } from '@atlassian/help-center-common-component/fields/reporter';
import { RequestCreateAttachmentField } from '@atlassian/help-center-common-component/fields/request-create-attachment-field';
import { RichTextArea } from '@atlassian/help-center-common-component/fields/rich-text-area';
import { SingleSelectField } from '@atlassian/help-center-common-component/fields/single-select-field';
import { SummaryField } from '@atlassian/help-center-common-component/fields/summary';
import { TextArea } from '@atlassian/help-center-common-component/fields/text-area';
import { TextField } from '@atlassian/help-center-common-component/fields/text-field';
import type { AdditionalFieldData } from '@atlassian/help-center-common-component/fields/types';
import { UnknownField } from '@atlassian/help-center-common-component/fields/unknown-field';
import type { User } from '@atlassian/help-center-common-component/fields/user-picker';
import { UserPicker } from '@atlassian/help-center-common-component/fields/user-picker';
import type { ForgeRequestCreatePropertyFormData } from '@atlassian/help-center-common-component/forge-ui/types';
import { SkeletonLoadingWrapper } from '@atlassian/help-center-common-component/loading';
import { RecaptchaForm } from '@atlassian/help-center-common-component/recaptcha-form';
import { ThemedButton } from '@atlassian/help-center-common-component/themed-button';
import { withAsync } from '@atlassian/help-center-common-component/with-async';
import { withFadeIn } from '@atlassian/help-center-common-component/with-fade-in';
import { FireScreenEventOnMount } from '@atlassian/help-center-common-util/analytics/fire-screen-event';
import { createPlaceholderValueMap } from '@atlassian/help-center-common-util/cmdb-object-picker';
import memoizeOne from '@atlassian/help-center-common-util/memoize-one';
import { FormErrorMessage } from '../form-error-message';
import type { FormErrors } from '../types';
import type { FieldType, ForgeFieldState } from 'state/persisted/request-create/types';

export interface Props extends Partial<DefaultProps> {
    accountId?: string;
    projectId?: string;
    portalId: number;
    requestTypeId: number;
    knowledgeBase?: RequestCreateKBState;
    fields: FieldState[];
    fieldValues: FieldValues;
    urlParamFieldValueOverrides: FieldValues;
    formRef?: React.RefObject<HTMLFormElement>;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onFieldChange: (name: string, value: any, notFromUserInteraction?: boolean, additionalFieldData?: object) => void;
    onFieldBlurredByUser?: ({
        field,
        value,
    }: {
        field: string;
        value: unknown;
        additionalFieldData?: { rawObject: object; rawValue: unknown };
    }) => void;
    onSubmit: (e: React.FormEvent<HTMLFormElement>) => void;
    onToggleFormExpanded: () => void;
    onCancel: () => void;
    enableSubmitButton: () => void;
    disableSubmitButton: () => void;
    onHoverSubmitButton?: () => void;
    className?: string;
    submitting?: boolean;
    submitDisabled: boolean;
    captchaEnabled: boolean;
    isCSM?: boolean;
    sitekey?: string;
    errors: FormErrors | undefined;
    submissionErrorCount: number;
    canCreateUsers: boolean;
    dateFormat?: string;
    dateTimeFormat?: string;
    firstDay?: number;
    useISO8601WeekNumbers?: boolean;
    isFormExpanded?: boolean;
    blockFormSubmission: (blocking: boolean) => void;
    isAttachmentUploading: boolean;
    registerProformaForm?: RegisterProformaForm;
    locale: string;
    updateForgeRequestPropertyData: (data: ForgeRequestCreatePropertyFormData) => void;
}

interface DefaultProps {
    errors: FormErrors;
}

type PropsWithDefaultProps = Props & DefaultProps;

interface State {
    accountId: string | undefined;
    isNewReporter: boolean | undefined;
    asyncFormCreateAppLoaded: boolean;
    shouldHoldUFO: boolean;
}

export class RequestFormDumb extends React.Component<Props & WithAnalyticsEventsProps, State> {
    static defaultProps: DefaultProps = {
        errors: {
            fieldErrors: {},
        },
    };

    state = {
        accountId: undefined,
        isNewReporter: undefined,
        asyncFormCreateAppLoaded: false,
        shouldHoldUFO: true,
    };

    unknownField: string | null = null;

    AsyncFormCreateAppInternal = withAsync({
        LoadingSkeleton: SkeletonLoadingWrapper,
        loader: lazyForPaint(() =>
            import(
                /* webpackChunkName: "proforma-form-create-app" */ '../../common/fields/proforma/form-create-app'
            ).then((module) => {
                this.setState({ asyncFormCreateAppLoaded: true });
                return withFadeIn()(module.FormCreateApp);
            })
        ),
    });

    onReporterFieldChange = (
        name: string,
        value: string,
        _: boolean = false,
        additionalFieldData: AdditionalFieldData<User>
    ) => {
        const accountId =
            additionalFieldData && additionalFieldData.rawObject && additionalFieldData.rawObject.accountId;
        const isNewReporter = name === 'reporter' && additionalFieldData?.rawObject?.isNew;
        this.setState({ accountId, isNewReporter });
        this.props.onFieldChange(name, value);
    };

    onRecaptchaTokenChange = (token: string | null) => {
        this.props.onFieldChange('g-recaptcha-response', token);
    };

    shouldComponentUpdate(nextProps: Props, nextState: State) {
        // shouldComponentUpdate override is implemented for performance improvement, see FSD-2741
        const { fieldValues: nextFieldValues, ...nextRemainingProps } = nextProps;
        const { fieldValues, ...remainingProps } = this.props;

        if (!deepEquals(this.state, nextState) || !deepEquals(remainingProps, nextRemainingProps)) {
            return true;
        }

        // The organisation picker is dependent on the reporter field, so we should rerender in this case
        return nextFieldValues.reporter !== fieldValues.reporter;
    }

    componentDidMount() {
        const { createAnalyticsEvent } = this.props;

        if (createAnalyticsEvent) {
            const analyticsEvent = createAnalyticsEvent({
                analyticsType: OPERATIONAL_EVENT_TYPE,
                action: this.unknownField ? 'failed' : 'loaded',
                actionSubjectId: 'fields',
                attributes: {
                    unknownField: this.unknownField,
                },
            });
            analyticsEvent.fire();
        }
    }

    renderFields(fields: FieldState[], submitting: boolean) {
        return fields.filter((field) => field.displayed).map((field) => this.renderField(field, submitting));
    }

    renderField(fieldState: FieldState, submitting: boolean) {
        const { type, value: _, ...baseProps } = fieldState;
        delete baseProps.valueOverride;

        const {
            projectId,
            portalId,
            requestTypeId,
            onFieldChange,
            knowledgeBase,
            enableSubmitButton,
            disableSubmitButton,
            canCreateUsers,
            locale,
            dateFormat,
            dateTimeFormat,
            firstDay,
            useISO8601WeekNumbers,
            fieldValues,
            urlParamFieldValueOverrides,
            fields,
        } = this.props as PropsWithDefaultProps;

        const fieldErrors = (this.props.errors && this.props.errors.fieldErrors) || {};
        const errorReasonKey = this.props.errors && this.props.errors.errorReasonKey;
        // Suppressing existing violation. Please fix this.
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        const urlParamValueOverride = urlParamFieldValueOverrides[fieldState.id];
        const { reporter } = this.props.fieldValues;

        switch (type) {
            case 'text':
                const textState = fieldState as TextFieldState;
                if (textState.id === 'summary') {
                    return (
                        <SummaryField
                            {...baseProps}
                            requestTypeId={
                                FeatureGates.checkGate('remove-linked-spaces-check-for-article-suggestions')
                                    ? requestTypeId
                                    : undefined
                            }
                            knowledgeBase={knowledgeBase}
                            // We need to rehydrate the default value if its defined.
                            // If we don't do this the internal state is thrown away when re-rendered
                            // and the field can never get back the current value.
                            // This is currently handling the case when the summary field re-renders
                            // from request tour onboarding dismissal.
                            defaultValue={calculateDefaultSummaryValue(
                                urlParamValueOverride,
                                fieldValues[textState.id],
                                textState.value
                            )}
                            valueOverride={isUiModificationsSupportEnabled() ? textState.valueOverride : undefined}
                            onChange={onFieldChange}
                            onFieldBlurredByUser={this.props.onFieldBlurredByUser}
                            key={textState.id}
                            errorMessage={fieldErrors[textState.id]}
                            disabled={submitting || !!textState.disabled}
                        />
                    );
                }
                if (textState.id === 'email') {
                    return (
                        <AnonymousEmailField
                            {...baseProps}
                            key={textState.id}
                            // Suppressing existing violation. Please fix this.
                            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
                            defaultValue={urlParamValueOverride === undefined ? textState.value : urlParamValueOverride}
                            onChange={onFieldChange}
                            errorMessage={fieldErrors.email}
                            disabled={submitting || !!textState.disabled}
                            errorReasonKey={errorReasonKey}
                        />
                    );
                }

                return (
                    <TextField
                        {...baseProps}
                        // Suppressing existing violation. Please fix this.
                        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
                        defaultValue={urlParamValueOverride === undefined ? textState.value : urlParamValueOverride}
                        onChange={onFieldChange}
                        key={textState.id}
                        errorMessage={fieldErrors[textState.id]}
                        disabled={submitting || !!textState.disabled}
                        updatedTextField={isNewTextFieldEnabledForForm()}
                        testId={'request-create-form-text-field'}
                    />
                );

            case 'checkbox':
                const checkboxState = fieldState as CheckboxGroupState;
                const checkboxItems = checkboxState.items.map((item) => {
                    return {
                        label: item.label,
                        value: item.value,
                    };
                });
                return (
                    <CheckboxGroup
                        {...baseProps}
                        items={checkboxItems}
                        // Suppressing existing violation. Please fix this.
                        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
                        defaultValue={urlParamValueOverride || checkboxState.value}
                        onChange={onFieldChange}
                        key={checkboxState.id}
                        errorMessage={fieldErrors[checkboxState.id]}
                        disabled={submitting || !!checkboxState.disabled}
                    />
                );

            case 'textarea':
                const textAreaState = fieldState as TextAreaState;
                return isRichTextAreaEnabled() && textAreaState.rendererType === 'atlassian-wiki-renderer' ? (
                    <RichTextArea
                        {...baseProps}
                        onChange={onFieldChange}
                        defaultValue={calculateDefaultRichTextValue(
                            urlParamValueOverride,
                            fieldValues[textAreaState.id],
                            textAreaState.value
                        )}
                        key={textAreaState.id}
                        errorMessage={fieldErrors[textAreaState.id]}
                        disabled={submitting || !!textAreaState.disabled}
                        fieldConfigs={{
                            portalId: this.props.portalId,
                            fieldConfigId: textAreaState.fieldConfigId,
                            fieldName: textAreaState.id,
                        }}
                    />
                ) : (
                    <TextArea
                        {...baseProps}
                        minimumRows={6}
                        onChange={onFieldChange}
                        // Suppressing existing violation. Please fix this.
                        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
                        defaultValue={urlParamValueOverride === undefined ? textAreaState.value : urlParamValueOverride}
                        key={textAreaState.id}
                        errorMessage={fieldErrors[textAreaState.id]}
                        disabled={submitting || !!textAreaState.disabled}
                    />
                );

            case 'radio':
                const radioGroupState = fieldState as RadioGroupState;
                const radioItems = radioGroupState.items.map((item) => {
                    return {
                        label: item.label,
                        name: item.name,
                        value: item.value,
                    };
                });
                return (
                    <RadioGroup
                        {...baseProps}
                        items={radioItems}
                        // Suppressing existing violation. Please fix this.
                        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
                        defaultValue={urlParamValueOverride || radioGroupState.value}
                        onChange={onFieldChange}
                        key={radioGroupState.id}
                        errorMessage={fieldErrors[radioGroupState.id]}
                        disabled={submitting || !!radioGroupState.disabled}
                    />
                );

            case 'date':
            case 'duedate':
                const dateState = fieldState as DateState;
                return (
                    <DatePicker
                        {...baseProps}
                        // Suppressing existing violation. Please fix this.
                        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
                        defaultValue={urlParamValueOverride || dateState.defaultDate}
                        onChange={onFieldChange}
                        key={dateState.id}
                        errorMessage={fieldErrors[dateState.id]}
                        disabled={submitting || !!dateState.disabled}
                        dateFormat={dateFormat}
                        firstDay={firstDay}
                        locale={locale}
                        useISO8601WeekNumbers={useISO8601WeekNumbers}
                    />
                );

            case 'datetime':
                const dateTimeState = fieldState as DateState;
                return (
                    <DateTimePicker
                        {...baseProps}
                        // Suppressing existing violation. Please fix this.
                        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
                        defaultValue={urlParamValueOverride || dateTimeState.defaultDate}
                        onChange={onFieldChange}
                        key={dateTimeState.id}
                        errorMessage={fieldErrors[dateTimeState.id]}
                        disabled={submitting || !!dateTimeState.disabled}
                        dateTimeFormat={dateTimeFormat}
                        firstDay={firstDay}
                        useISO8601WeekNumbers={useISO8601WeekNumbers}
                        locale={locale}
                    />
                );

            case 'attachment':
                const attachmentState = fieldState as AttachmentState;
                return (
                    <RequestCreateAttachmentField
                        {...baseProps}
                        onChange={onFieldChange}
                        key={attachmentState.id}
                        disabled={submitting || !!attachmentState.disabled}
                        errorMessage={fieldErrors[attachmentState.id]}
                        portalId={this.props.portalId}
                        blockFormSubmission={this.props.blockFormSubmission}
                    />
                );

            case 'reporter':
                const reporterState = fieldState as ReporterState;
                const defaultReporter = reporterState.defaultReporter
                    ? {
                          email: reporterState.defaultReporter.emailAddress,
                          iconUrl: reporterState.defaultReporter.avatar,
                          value: reporterState.defaultReporter.id,
                          label: reporterState.defaultReporter.displayName,
                          accountId: reporterState.defaultReporter.accountId,
                      }
                    : undefined;

                return (
                    <Reporter
                        {...baseProps}
                        portalId={this.props.portalId}
                        label={reporterState.label || ''}
                        onChange={this.onReporterFieldChange}
                        key={reporterState.id}
                        errorMessage={fieldErrors[reporterState.id]}
                        disabled={submitting || !!reporterState.disabled}
                        defaultReporter={defaultReporter}
                        canCreateUser={canCreateUsers}
                        createLabelType="create"
                        fieldConfigId={reporterState.fieldConfigId}
                    />
                );

            case 'userpicker':
                const userPickerState = fieldState as UserPickerState;
                const defaultUser = userPickerState.defaultUser
                    ? {
                          email: userPickerState.defaultUser.emailAddress,
                          iconUrl: userPickerState.defaultUser.avatar,
                          value: userPickerState.defaultUser.id,
                          label: userPickerState.defaultUser.displayName,
                          accountId: userPickerState.defaultUser.accountId,
                      }
                    : undefined;

                return (
                    <UserPicker
                        {...baseProps}
                        portalId={this.props.portalId}
                        label={userPickerState.label || ''}
                        onChange={onFieldChange}
                        key={userPickerState.id}
                        errorMessage={fieldErrors[userPickerState.id]}
                        disabled={submitting || !!userPickerState.disabled}
                        defaultUser={defaultUser}
                        canCreateUser={canCreateUsers}
                        createLabelType="add"
                        fieldConfigId={userPickerState.fieldConfigId}
                    />
                );

            case 'multiuserpicker':
                const multiUserPickerState = fieldState as MultiUserPickerState;
                const defaultUsers = (multiUserPickerState.defaultUsers || []).map((user) => ({
                    email: user.emailAddress,
                    iconUrl: user.avatar,
                    value: user.id,
                    label: user.displayName,
                }));

                return (
                    <MultiUserPicker
                        {...baseProps}
                        portalId={this.props.portalId}
                        label={multiUserPickerState.label || ''}
                        onChange={onFieldChange}
                        key={multiUserPickerState.id}
                        errorMessage={fieldErrors[multiUserPickerState.id]}
                        disabled={submitting || !!multiUserPickerState.disabled}
                        defaultUsers={defaultUsers}
                        fieldConfigId={multiUserPickerState.fieldConfigId}
                        canAddUserByEmailAddress={canCreateUsers}
                    />
                );

            case 'labelpicker':
                const labelPickerState = fieldState as LabelPickerState;
                return (
                    <LabelPicker
                        {...baseProps}
                        customFieldId={labelPickerState.customFieldId}
                        portalId={portalId}
                        requestTypeId={requestTypeId}
                        onChange={onFieldChange}
                        key={labelPickerState.id}
                        errorMessage={fieldErrors[labelPickerState.id]}
                        disabled={submitting || !!labelPickerState.disabled}
                        // Suppressing existing violation. Please fix this.
                        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
                        defaultLabels={urlParamValueOverride || labelPickerState.defaultLabels}
                    />
                );

            case 'organisationpicker':
                const orgState = fieldState as OrganisationPickerState;

                return (
                    // @ts-ignore
                    <OrganisationPicker
                        {...baseProps}
                        projectId={projectId}
                        portalId={portalId}
                        requestTypeId={requestTypeId}
                        onChange={onFieldChange}
                        key={orgState.id}
                        // Suppressing existing violation. Please fix this.
                        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
                        organisationUserEmail={reporter}
                        isNewReporter={this.state.isNewReporter}
                        disabled={submitting || !!orgState.disabled}
                        enableSubmitButton={enableSubmitButton}
                        disableSubmitButton={disableSubmitButton}
                        shareWithOrgByDefault={orgState.shareWithOrgByDefault}
                    />
                );

            case 'assetspicker':
                const assetPickerState = fieldState as AssetsPickerState;
                return (
                    <AssetsPicker
                        {...baseProps}
                        label={assetPickerState.label || ''}
                        onChange={onFieldChange}
                        key={assetPickerState.id}
                        disabled={submitting || !!assetPickerState.disabled}
                        fieldConfigId={assetPickerState.fieldConfigId}
                        accountId={this.state.accountId}
                        portalId={portalId}
                        errorMessage={fieldErrors[assetPickerState.id]}
                    />
                );

            case 'multiservicepicker':
                const multiServicePickerState = fieldState as MultiServicePickerState;

                return (
                    <MultiServicePicker
                        {...baseProps}
                        label={multiServicePickerState.label || ''}
                        onChange={onFieldChange}
                        key={multiServicePickerState.id}
                        disabled={submitting || !!multiServicePickerState.disabled}
                        errorMessage={fieldErrors[multiServicePickerState.id]}
                    />
                );

            case 'cmdbobjectpicker':
                const cmdbObjectPickerState = fieldState as CmdbObjectPickerState;
                const fieldValueMapReducer = (accumulator: string[][], currentField: FieldState): string[][] => {
                    // Suppressing existing violation. Please fix this.
                    // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
                    return accumulator.concat([[currentField.id, fieldValues[currentField.id]?.toString() || '']]);
                };

                // Disable field if form is submitting
                // OR if the field does not have a valid configuration (and therefore, cannot provide any values to pick)
                const isCmdbObjectPickerDisabled =
                    submitting || !cmdbObjectPickerState.hasValidConfig || !!cmdbObjectPickerState.disabled;

                return (
                    <CmdbObjectPicker
                        {...baseProps}
                        {...cmdbObjectPickerState}
                        onChange={onFieldChange}
                        key={cmdbObjectPickerState.id}
                        disabled={isCmdbObjectPickerDisabled}
                        errorMessage={fieldErrors[cmdbObjectPickerState.id]}
                        portalId={portalId}
                        requestTypeId={requestTypeId}
                        fieldValueMap={fields.reduce(fieldValueMapReducer, [])}
                        placeholderValues={createPlaceholderValueMap(
                            fieldValues,
                            cmdbObjectPickerState.placeholderKeys
                        )}
                    />
                );

            case 'select':
                const selectState = fieldState as SelectState;
                return (
                    <SingleSelectField
                        {...baseProps}
                        label={selectState.label || ''}
                        onChange={onFieldChange}
                        key={selectState.id}
                        errorMessage={fieldErrors[selectState.id]}
                        disabled={submitting || !!selectState.disabled}
                        // Suppressing existing violation. Please fix this.
                        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
                        defaultValue={urlParamValueOverride || selectState.defaultValue}
                        valueOverride={isUiModificationsSupportEnabled() ? selectState.valueOverride : undefined}
                        options={selectState.options}
                    />
                );

            case 'multiselect':
                const multiSelectState = fieldState as MultiSelectState;
                return (
                    <MultiSelectField
                        {...baseProps}
                        label={multiSelectState.label || ''}
                        onChange={onFieldChange}
                        key={multiSelectState.id}
                        errorMessage={fieldErrors[multiSelectState.id]}
                        disabled={submitting || !!multiSelectState.disabled}
                        // Suppressing existing violation. Please fix this.
                        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
                        defaultValue={urlParamValueOverride || multiSelectState.defaultValue}
                        options={multiSelectState.options}
                    />
                );

            case 'cascadingselect':
                const cascadingSelectState = fieldState as CascadingSelectState;
                return (
                    <CascadingSelect
                        {...baseProps}
                        actionSubjectId="cascadingSelectField"
                        label={cascadingSelectState.label || ''}
                        onChange={onFieldChange}
                        key={cascadingSelectState.id}
                        errorMessage={fieldErrors[cascadingSelectState.id]}
                        disabled={submitting || !!cascadingSelectState.disabled}
                        // Suppressing existing violation. Please fix this.
                        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
                        defaultValue={urlParamValueOverride || cascadingSelectState.value}
                        options={cascadingSelectState.options}
                    />
                );

            case 'entitlementpicker':
                const entitlementState = fieldState as EntitlementPickerState;

                return (
                    <EntitlementPicker
                        {...baseProps}
                        onChange={onFieldChange}
                        label={entitlementState.label || ''}
                        value={fieldValues[entitlementState.id] as string}
                        key={entitlementState.id}
                        reporterId={reporter as string}
                        isNewReporter={this.state.isNewReporter}
                        disabled={submitting || !!entitlementState.disabled}
                        enableSubmitButton={enableSubmitButton}
                        disableSubmitButton={disableSubmitButton}
                        errorMessage={fieldErrors[entitlementState.id]}
                    />
                );

            case 'forge':
                if (!isForgeCustomFieldsOnPortalRequestEnabled()) {
                    this.unknownField = type;
                    return this.renderUnknownField(fieldState as unknown as UnknownState);
                }

                const forgeState = fieldState as ForgeFieldState;
                return (
                    <ForgeField
                        {...baseProps}
                        jiraFieldType={forgeState.jiraFieldType as string} // TODO: remove as with ditto_uim_-_jsm_support_main FF cleanup
                        key={forgeState.id}
                        entryPoint="edit"
                        portalId={this.props.portalId}
                        requestTypeId={this.props.requestTypeId}
                        errorMessage={fieldErrors[forgeState.id]}
                        fallbackRender={(newType: FieldType) => {
                            return this.renderField({ ...fieldState, type: newType } as FieldState, submitting);
                        }}
                        disabled={submitting || !!fieldState.disabled}
                        enableSubmitButton={enableSubmitButton}
                        disableSubmitButton={disableSubmitButton}
                        onChange={onFieldChange}
                    />
                );

            default:
                const unknownFieldProps = fieldState as UnknownState;
                this.unknownField = type;
                return this.renderUnknownField(unknownFieldProps);
        }
    }

    renderUnknownField(fieldState: FieldState) {
        return <UnknownField id={fieldState.id} key={fieldState.id} />;
    }

    renderErrorMessage() {
        const { submissionErrorCount, errors } = this.props;

        if (errors && errors.submissionError) {
            return <AutoFocus key={submissionErrorCount}>{<FormErrorMessage errors={errors} />}</AutoFocus>;
        }

        return null;
    }

    renderFormFinished() {
        if (this.state.shouldHoldUFO) {
            this.setState({
                shouldHoldUFO: false,
            });
        }
    }

    render() {
        const {
            accountId,
            fields,
            fieldValues,
            errors,
            className,
            submitting,
            submitDisabled,
            onSubmit,
            onCancel,
            sitekey,
            requestTypeId,
            portalId,
            isFormExpanded,
            isAttachmentUploading,
            registerProformaForm,
            locale,
            knowledgeBase,
            updateForgeRequestPropertyData,
            formRef,
        } = this.props;

        const hasProformaForm = !!registerProformaForm;
        const AsyncFormCreateAppInternal = this.AsyncFormCreateAppInternal;
        // If Proforma is on the page, display the Jira and Proforma fields at the same time
        const renderJiraFields = hasProformaForm ? this.state.asyncFormCreateAppLoaded : true;

        return (
            <RecaptchaForm
                // eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Disabled to rollout go/ui-styling-standard tooling, please resolve
                className={className}
                recaptchaEnabled={this.props.captchaEnabled}
                onSubmit={onSubmit}
                sitekey={sitekey}
                onTokenChange={this.onRecaptchaTokenChange}
                formRef={isForgeCustomFieldsOnPortalRequestEnabled() ? formRef : undefined}
                noValidate
            >
                {({ isExecuting }) => {
                    const isProcessing = submitting || isExecuting;
                    const isButtonDisable = isProcessing || submitDisabled || isAttachmentUploading;

                    // Reporter of issue is the one selected in the reporter user picker.
                    // If reporter user picker is not displayed, then the reporter is the current user.
                    // Suppressing existing violation. Please fix this.
                    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
                    const reporterAccountId = fieldValues.reporter ?? accountId;

                    const proformaFormView = hasProformaForm && (
                        <AsyncFormCreateAppInternal
                            portalId={portalId}
                            requestTypeId={requestTypeId}
                            registerProformaForm={registerProformaForm}
                            locale={locale}
                            knowledgeBase={knowledgeBase}
                            // Suppressing existing violation. Please fix this.
                            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
                            reporterAccountId={reporterAccountId}
                            fieldErrors={errors?.fieldErrors}
                            data-testid={'AsyncFormCreateAppInternal'}
                            blockFormSubmission={this.props.blockFormSubmission}
                            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
                            renderFormFinished={this.renderFormFinished.bind(this)}
                        />
                    );

                    return (
                        <>
                            {this.renderErrorMessage()}

                            <SmoothCollapse allowOverflowWhenOpen={isRichTextAreaEnabled()} expanded={!!isFormExpanded}>
                                {renderJiraFields && this.renderFields(fields, isProcessing)}
                                {!isProformaTTVCUpdateMeasureEnabled() && proformaFormView}
                                {isProformaTTVCUpdateMeasureEnabled() && (
                                    <UFOLoadHold name="proforma-jira-portal-create" hold={this.state.shouldHoldUFO}>
                                        {proformaFormView}
                                    </UFOLoadHold>
                                )}

                                <ConnectFields
                                    requestTypeId={requestTypeId}
                                    portalId={portalId}
                                    location={REQUEST_CREATE_PANEL}
                                />

                                <RequestCreatePropertyPanelModule
                                    portalId={this.props.portalId}
                                    projectId={toNumber(this.props.projectId)}
                                    requestTypeId={this.props.requestTypeId}
                                    onUpdateRequestPropertyData={updateForgeRequestPropertyData}
                                />

                                {renderJiraFields && (
                                    <ButtonGroup>
                                        <ThemedButton
                                            appearance="primary"
                                            type="submit"
                                            isDisabled={isButtonDisable}
                                            // eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Disabled to rollout go/ui-styling-standard tooling, please resolve
                                            className="request-create-form-submit"
                                            actionSubjectId="submitRequest"
                                            interactionName="request-create-submit"
                                            onMouseEnter={this.props.onHoverSubmitButton}
                                            testId="request-create-form-submit-button"
                                        >
                                            <FormattedMessage {...i18n.sendForm} />
                                        </ThemedButton>
                                        <FireScreenEventOnMount />

                                        <ThemedButton
                                            appearance="subtle"
                                            isDisabled={isButtonDisable}
                                            onClick={onCancel}
                                            actionSubjectId="cancelRequest"
                                            testId="request-create-form-cancel-button"
                                        >
                                            <FormattedMessage {...i18n.cancelForm} />
                                        </ThemedButton>

                                        {(isProcessing || isAttachmentUploading) && <Spinner size="small" />}
                                    </ButtonGroup>
                                )}
                            </SmoothCollapse>
                        </>
                    );
                }}
            </RecaptchaForm>
        );
    }
}
export default withAnalyticsContext({
    componentName: 'requestCreateForm',
})(withAnalyticsEvents()(RequestFormDumb));

const calculateDefaultSummaryValue = (
    urlParamOverride?: string,
    fieldValue?: string,
    defaultValueFromState?: string
): string | undefined => {
    return urlParamOverride ?? fieldValue ?? defaultValueFromState;
};

const getRichTextAreaFieldValue = (
    urlParamOverride?: string,
    fieldValue?: string,
    defaultValueFromState?: string
): string | undefined => {
    return urlParamOverride || fieldValue || defaultValueFromState || undefined;
};

const calculateDefaultRichTextValue = memoizeOne(getRichTextAreaFieldValue);
